/**
 * useLookupEnumLabelColumn — the "Label column" state machine for the declare
 * dialog's `lookup` mode.
 *
 * A lookup enum stores KEYS; the human-readable name comes from a column on
 * the referenced table. That label column is NOT part of the declaration — it
 * lives in the shared, per-table `DatabaseMetaInfo.tableDisplayColumns` record
 * so one change updates every loose FK and lookup enum pointing at the table.
 *
 * Resolution order (identical to `LooseRelationshipTargetPicker`'s):
 *   1. the user's pick in this dialog (once they touch the dropdown),
 *   2. the persisted `tableDisplayColumns` entry (`''` = explicit none),
 *   3. the `pickDisplayColumn` name heuristic over the target's columns.
 *
 * The persisted record is written **only when the user actually touched the
 * dropdown** — a prefilled heuristic value isn't a deliberate choice and
 * shouldn't burn a write (same rule as the loose-FK editor).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { EnumLookupTarget } from '../../../../../types/electron';
import { hostMetadataQueryKey, type TableExplorerMetadataEditorProviders } from '../../../table-explorer/components/tableExplorerHostPorts';
import { useMetaInfo } from '../../hooks/useMetaInfo';
import { upsertTableDisplayColumn } from '../../services/metaInfoApi';
import { findTableDisplayColumn } from '../../utils/tableDisplayColumn';
import {
  LOOKUP_TARGET_COLUMNS_STALE_MS,
  fetchLookupTargetColumns,
  lookupTargetColumnsQueryKey,
  resolveLookupLabelColumn,
} from '../../utils/lookupLabelColumn';

/** Mirrors the sentinel `LooseFkDisplayColumnSelect` renders for "(none)". */
const NONE_SENTINEL = '__none__';

export type LookupEnumLabelColumn = {
  /** Target-table columns for the dropdown; `(none)` is added by the select. */
  columnOptions: { value: string; label: string }[];
  loading: boolean;
  /** SearchableSelect value: `''` (nothing resolved), `'__none__'`, or a column. */
  selectValue: string;
  /** The column to fetch labels from — `null` when none is resolved / chosen. */
  labelColumn: string | null;
  /**
   * False while the target's column list is still loading. The preview fetch
   * waits on this so it never fires once with no label and again with one.
   */
  ready: boolean;
  /**
   * True when the target's column list could not be read (S4 review D, D5) — `labelColumn` is then `null` for
   * lack of information, not by choice, and a refresh must not replace named pairs with keys-only ones.
   */
  columnsUnavailable: boolean;
  onChange: (next: string) => void;
  /**
   * Persist the shared `tableDisplayColumns` entry when the user changed it.
   * Resolves to what happened so the caller can fire analytics: `'set'`,
   * `'cleared'`, or `null` (untouched / unchanged — nothing written).
   */
  persistIfTouched: () => Promise<'set' | 'cleared' | null>;
};

export function useLookupEnumLabelColumn(args: {
  connectionId: string | null;
  database: string | null;
  /** Absent when the dialog isn't in lookup mode — the hook stays inert. */
  target: EnumLookupTarget | undefined;
  /**
   * Host metadata provider (Azure Table, S4 review C, M7): the target's
   * columns come from it instead of the SQL `getTableMetadata` IPC. Absent =
   * every SQL engine, unchanged (same shared query key as before).
   */
  metadataEditors?: TableExplorerMetadataEditorProviders;
}): LookupEnumLabelColumn {
  const { connectionId, database, target, metadataEditors } = args;
  const { data: metaInfo } = useMetaInfo(connectionId, database);
  // `undefined` = pristine (fall through to persisted entry / heuristic);
  // `null` = the user deliberately picked "(none)"; `string` = their pick.
  const [picked, setPicked] = useState<string | null | undefined>(undefined);
  const [touched, setTouched] = useState(false);

  const toSchema = target?.toSchema;
  const toTable = target?.toTable;
  const toColumn = target?.toColumn;

  // A different target table means a different label-column question — drop
  // the local pick so the persisted entry / heuristic runs again.
  useEffect(() => {
    setPicked(undefined);
    setTouched(false);
  }, [toSchema, toTable]);

  // Same query key as the loose-FK target picker and the miss-driven cache
  // self-heal so all three share one cache entry.
  const columnsQuery = useQuery({
    queryKey: metadataEditors
      ? hostMetadataQueryKey(metadataEditors, connectionId, 'lookup-target-columns', database, toSchema, toTable)
      : lookupTargetColumnsQueryKey(connectionId, database, toSchema, toTable),
    queryFn: async (): Promise<string[]> =>
      metadataEditors
        ? (await metadataEditors.listColumns({
            connectionId: connectionId ?? '',
            database: database ?? '',
            schema: toSchema ?? '',
            table: toTable ?? '',
          })).map((column) => column.name)
        : fetchLookupTargetColumns({
            connectionId,
            database,
            schema: toSchema,
            table: toTable,
          }),
    enabled: !!connectionId && !!database && toTable != null,
    staleTime: LOOKUP_TARGET_COLUMNS_STALE_MS,
  });

  const columns = useMemo(() => columnsQuery.data ?? [], [columnsQuery.data]);

  const persistedEntry = useMemo(
    () =>
      toTable == null
        ? undefined
        : findTableDisplayColumn(metaInfo ?? null, toSchema ?? '', toTable),
    [metaInfo, toSchema, toTable],
  );

  const labelColumn = useMemo<string | null>(() => {
    // The user's own pick wins over the shared resolution below.
    if (picked !== undefined) return picked;
    if (toTable == null) return null;
    return resolveLookupLabelColumn({
      metaInfo,
      schema: toSchema,
      table: toTable,
      keyColumn: toColumn,
      columns,
    });
  }, [picked, metaInfo, toSchema, toTable, toColumn, columns]);

  const selectValue =
    labelColumn !== null
      ? labelColumn
      : picked === null || persistedEntry?.column === ''
        ? NONE_SENTINEL
        : '';

  const onChange = useCallback((next: string) => {
    setPicked(next === NONE_SENTINEL ? null : next);
    setTouched(true);
  }, []);

  const persistIfTouched = useCallback(async (): Promise<'set' | 'cleared' | null> => {
    if (!touched || !connectionId || !database || toTable == null) return null;
    const nextValue = labelColumn ?? '';
    if (persistedEntry?.column === nextValue) return null;
    await upsertTableDisplayColumn(connectionId, database, {
      schema: toSchema ?? '',
      table: toTable,
      column: nextValue,
      createdAt: new Date().toISOString(),
    });
    return nextValue === '' ? 'cleared' : 'set';
  }, [touched, connectionId, database, toSchema, toTable, labelColumn, persistedEntry]);

  return {
    columnOptions: useMemo(
      () => columns.map((name) => ({ value: name, label: name })),
      [columns],
    ),
    loading: columnsQuery.isLoading,
    selectValue,
    labelColumn,
    ready: toTable == null || !columnsQuery.isLoading,
    columnsUnavailable: toTable != null && columnsQuery.isError,
    onChange,
    persistIfTouched,
  };
}
